<script setup lang="ts">
import { useTaskLoader } from '@/composeables';
import {
    ContentComp,
    DataSourceLookup,
    DateComp,
    DisplayBool,
    DisplayBoolRadio,
    InputBool,
    InputRadio,
    ToolbarComp,
    ValidatedInt,
    ValidatedText,
    WizardComp,
} from '..';
import { nextTick, ref } from 'vue';
import { IntArray, Participant } from '@/objects/generated';
import { ParticipantDao } from '@/dao';
import { GridColumnDefinition, GridColumnOptions, GridSetup, type IGridCompExposed, GridDataSource } from '../Grid/Classes';
import type { IGridRowGetterParams } from '../Grid/Classes/GridDataSource';
import { propertyof } from '@/utilities';
import GridComp from '../Grid/GridComp.vue';
import { useCommonStore } from '@/stores';
import { DropdownItemButton, DropdownItemMenu } from '@/objects';
import { WizardContainer, WizardPageDefinition } from '@/objects/Wizard';
import DuplicateMergeDetails from './DuplicateMergeDetails.vue';
import WellKnown from '@/objects/generated/static/WellKnown';

/* type for participant id so it does not say number */
type ParticipantID = number;

const BUYPASSID = WellKnown.IDTYPE_BUYPASS_ID;
const UCIIID = WellKnown.IDTYPE_UCI_ID;

/** Genders */
const Genders = useCommonStore().Genders;

/** Loaders */
const { AddLoader, LoadState, ClearLoaders } = useTaskLoader();

/* handled participants */
let handled = new Array<ParticipantID>();

/* emits */
const emit = defineEmits<{
    (e: 'done', handled: Array<ParticipantID>): void;
}>();

/* Load participants */
const loadParticipants = (participantIDs: ParticipantID[]) => {
    ClearLoaders();
    AddLoader(async () => {
        const data = await ParticipantDao.Participants(participantIDs, false);
        participants.value = data;
        selectedAsMain.value = pickSelectedAsMain();
        selected.value = data.map((x) => x.ID);
    }, 'duplicates.loading_duplicates');
};

/* pick a selected as main
   we want the one with
   Buypassid
   if not the one with
   uciiid
   if not the one with
   username
   if not the one with the latest event
*/
const pickSelectedAsMain = () => {
    return [...participants.value].sort((a: Participant, b: Participant) => {
        const aBuypass = a.ParticipantIDs.find((x) => x.ParticipantIDTypeID == BUYPASSID);
        const bBuypass = b.ParticipantIDs.find((x) => x.ParticipantIDTypeID == BUYPASSID);
        if (aBuypass && !bBuypass) return -1;
        if (!aBuypass && bBuypass) return 1;

        const aUcii = a.ParticipantIDs.find((x) => x.ParticipantIDTypeID == UCIIID);
        const bUcii = b.ParticipantIDs.find((x) => x.ParticipantIDTypeID == UCIIID);
        if (aUcii && !bUcii) return -1;
        if (!aUcii && bUcii) return 1;

        if (a.Username && !b.Username) return -1;
        if (!a.Username && b.Username) return 1;

        if ((a.LastEventDate?.getTime() ?? 0) > (b.LastEventDate?.getTime() ?? 0)) return -1;
        if ((a.LastEventDate?.getTime() ?? 0) < (b.LastEventDate?.getTime() ?? 0)) return 1;

        return a.ID - b.ID;
    })[0].ID;
};

/* participants to select from */
const participants = ref<Participant[]>([]);

/* ids of participant we want to merge to */
const selectedAsMain = ref<ParticipantID>(0);
/* ids of selected participants */
const selected = ref<Array<ParticipantID>>([]);

/* Grid */
const grid = ref<IGridCompExposed<Participant> | null>(null);
/**
 * Set up the datasource
 */
const dataSource = new GridDataSource<Participant>({
    totalNumberOfRows: () => participants.value.length, // Maybe this needs to change for a server side data source

    rowGetter: (params: IGridRowGetterParams<Participant>) => {
        const slice = participants.value.slice(params.startRow, params.endRow);

        params.successCallback(slice, participants.value, participants.value.length);
    },
});

/**
 * Columns
 */
const gridColumnDefinitions = () => [
    new GridColumnOptions<Participant>({
        sortable: false,
        filterable: false,
        editable: true,
        dataField: 'NONE1', // this should be optional since we can send in valueGetter
        valueGetter: (p: Participant) => {
            return selected.value.includes(p.ID);
        },
        valueSetter: (p: Participant, val: boolean) => {
            if (val) {
                selected.value.push(p.ID);
            } else {
                selected.value = selected.value.filter((x) => x != p.ID);
            }
        },
        cellRenderer: DisplayBool,
        cellEditor: InputBool,
    }),
    new GridColumnOptions<Participant>({
        sortable: false,
        filterable: false,
        editable: true,
        dataField: 'NONE2', // this should be optional since we can send in valueGetter
        valueGetter: (p: Participant) => {
            return p.ID == selectedAsMain.value;
        },
        valueSetter: (p: Participant, val: boolean) => {
            if (val) {
                const old = selectedAsMain.value;
                selectedAsMain.value = p.ID;
                grid.value?.refreshCells([old.toString()]);
            }
        },
        cellRenderer: DisplayBoolRadio,
        // @ts-expect-error @ts-ignore
        cellEditor: InputRadio,
        cellEditorParams: { val: true },
    }),
    new GridColumnOptions<Participant>({
        sortable: true,
        filterable: true,
        editable: false,
        dataField: propertyof<Participant>('ID'),
        cellRenderer: ValidatedInt,
        columnHeaderText: 'duplicate.id',
        minWidth: 100,
        maxWidth: 100,
        resizable: false,
    }),
    new GridColumnOptions<Participant>({
        sortable: true,
        filterable: true,
        editable: false,
        dataField: propertyof<Participant>('Firstname'),
        cellRenderer: ValidatedText,
        columnHeaderText: 'duplicate.firstname',
    }),
    new GridColumnOptions<Participant>({
        sortable: true,
        filterable: true,
        editable: false,
        dataField: propertyof<Participant>('Surname'),
        cellRenderer: ValidatedText,
        columnHeaderText: 'duplicate.surname',
    }),
    new GridColumnOptions<Participant>({
        sortable: true,
        filterable: true,
        editable: false,
        minWidth: 50,
        width: 50,
        dataField: propertyof<Participant>('Gender'),
        cellRenderer: DataSourceLookup,
        cellRendererParams: { hideCaret: true, datasource: { mapsource: Genders } },
        columnHeaderText: 'duplicate.gender',
    }),
    new GridColumnOptions<Participant>({
        sortable: true,
        filterable: true,
        editable: false,
        minWidth: 95,
        width: 95,
        dataField: propertyof<Participant>('Birthday'),
        cellRenderer: DateComp,
        cellRendererParams: { format: 'DATE' },
        columnHeaderText: 'duplicate.birthdate',
    }),
    new GridColumnOptions<Participant>({
        sortable: true,
        filterable: true,
        editable: false,
        dataField: propertyof<Participant>('Email'),
        cellRenderer: ValidatedText,
        columnHeaderText: 'duplicate.email',
    }),
    new GridColumnOptions<Participant>({
        sortable: true,
        filterable: true,
        editable: false,
        dataField: propertyof<Participant>('Username'),
        cellRenderer: ValidatedText,
        columnHeaderText: 'duplicate.username',
    }),
    new GridColumnOptions<Participant>({
        sortable: true,
        filterable: true,
        editable: false,
        dataField: 'NONE3', // this should be optional since we can send in valueGetter
        valueGetter: (p: Participant) => {
            return `${p.Address1} ${p.Address2} ${p.PostalNumber} ${p.City}`;
        },
        cellRenderer: ValidatedText,
        columnHeaderText: 'duplicate.address',
    }),
    new GridColumnOptions<Participant>({
        sortable: true,
        filterable: true,
        editable: false,
        minWidth: 70,
        width: 70,
        dataField: propertyof<Participant>('EventCount'),
        cellRenderer: ValidatedInt,
        columnHeaderText: 'duplicate.eventcount',
    }),
    new GridColumnOptions<Participant>({
        sortable: true,
        filterable: true,
        editable: false,
        dataField: propertyof<Participant>('LastEvent'),
        cellRenderer: ValidatedText,
        columnHeaderText: 'duplicate.lastevent',
    }),
    new GridColumnOptions<Participant>({
        sortable: true,
        filterable: true,
        editable: false,
        dataField: propertyof<Participant>('LastChanged'),
        cellRenderer: DateComp,
        columnHeaderText: 'duplicate.lastchanged',
    }),
];

/**
 * Set up the grid
 */
const gridSetup = new GridSetup<Participant>({
    rowIdGetter: (row: Participant) => row.ID.toString(),
    datasource: dataSource,
    GridColumnDefinition: new GridColumnDefinition({
        CommonColumnOptions: {
            sortable: false,
            filterable: true,
            editable: true,
        },
        ColumnOptions: gridColumnDefinitions(),
    }),
    rowSelection: 'single',
    gridkey: 'duplicatemergegrid',
});

/* who we want to merge */
const toMerge = ref<Participant[]>([]);
/* the one we want to merge to */
const mergeTo = ref<Participant | null>(null);
/* calculate the above and go to merge view */
const merge = () => {
    toMerge.value = participants.value.filter((x) => selected.value.includes(x.ID));
    mergeTo.value = participants.value.find((x) => x.ID == selectedAsMain.value)!;
    wizard.value?.goto(wizardDefinition.WizardPages[1]);
};

/* merge done */
const merged = (merged: boolean) => {
    wizard.value?.goto(wizardDefinition.WizardPages[0]);
    if (merged) {
        handled = handled.concat(toMerge.value.map((x) => x.ID));
        updateHandled();
    }
};

/* mark as individuals */
const markAsIndividuals = (unchecked: boolean = false) => {
    AddLoader(async () => {
        const ids = unchecked ? participants.value.map((x) => x.ID).filter((x) => !selected.value.includes(x)) : selected.value;
        await ParticipantDao.MarkAsNotDuplicates(IntArray.FromJsValues({ Values: ids }));

        handled = handled.concat(ids);

        updateHandled();
    }, 'duplicates.marking_as_individuals');
};

/* handles who is handled, ex if we mark 2 as individuals they will disapear from list
    if list is empty we close the dialog */
const updateHandled = () => {
    participants.value = participants.value.filter((x) => !selected.value.includes(x.ID));

    if (participants.value.length == 0) {
        emit('done', handled);
        return;
    }

    selected.value = selected.value.filter((x) => !handled.includes(x));
    if (!selected.value.includes(selectedAsMain.value)) {
        selectedAsMain.value = participants.value[0].ID;
    }
    grid.value?.refreshCachedData();
};

/* close dialog */
const closeAction = DropdownItemButton.create({
    buttonType: 'Close',
    action: () => {
        emit('done', handled);
    },
    icon: 'fa-arrow-left',
    translationkey: 'general.back',
});

/* open merge dialog */
const mergeAction = DropdownItemButton.create({
    buttonType: 'Primary',
    icon: 'fa-merge',
    action: () => {
        merge();
    },
    translationkey: 'merge.merge',
});

/* open merge and on close close the dialog */
const mergeAndCloseAction = DropdownItemButton.create({
    buttonType: 'Secondary',
    icon: 'fa-merge',
    action: () => {
        merge();
        emit('done', handled);
    },
    translationkey: 'merge.merge_and_close',
});

/* mark as individuals */
const markAsIndividualsAction = DropdownItemButton.create({
    buttonType: 'Secondary',
    icon: 'fa-people-arrows',
    action: () => {
        markAsIndividuals();
    },
    translationkey: 'merge.mark_as_individuals',
});

/* merge and mark the rest as individuals, this will also close as there is noone left */
const mergeAndMarkAsIndividualsAction = DropdownItemButton.create({
    buttonType: 'Secondary',
    icon: 'fa-merge',
    action: () => {
        merge();
        markAsIndividuals(true);
    },
    translationkey: 'merge.merge_and_mark_rest_as_idividuals',
});

/* Actions for menu */
const actions = [
    DropdownItemMenu.create({
        icon: 'fa-screwdriver-wrench',
        dropdownItems: [mergeAction, markAsIndividualsAction, mergeAndMarkAsIndividualsAction, mergeAndCloseAction],
    }),
    mergeAction,
    markAsIndividualsAction,
];

/* Wizard to swap between views */
const wizardDefinition = new WizardContainer();
wizardDefinition.WizardPages = [new WizardPageDefinition('duplicatemergelist', ''), new WizardPageDefinition('duplicatemergemerge', '')];

/* Ref to wizard comp */
const wizard = ref<InstanceType<typeof WizardComp> | null>(null);

defineExpose({
    show: (participantIDs: ParticipantID[]) => {
        handled = [];
        loadParticipants(participantIDs);
    },
});
</script>
<template>
    <ContentComp
        :load-state="LoadState"
        :fillContent="true"
        class="h-100"
    >
        <div class="d-flex flex-column h-100">
            <WizardComp
                :wizardDefinition="wizardDefinition"
                :showPrevNext="false"
                :showHeader="false"
                ref="wizard"
                :useGivenSpace="true"
                class="h-100"
            >
                <template #wizardPage-duplicatemergelist>
                    <div class="d-flex flex-column h-100">
                        <ToolbarComp
                            class="mb-2"
                            :actions="actions"
                            :rightActions="[closeAction]"
                        >
                            <template #middle>
                                <span></span>
                            </template>
                        </ToolbarComp>
                        <GridComp
                            ref="grid"
                            :gridSetup="gridSetup"
                            class="flex-grow-1"
                        />
                    </div>
                </template>
                <template #wizardPage-duplicatemergemerge>
                    <DuplicateMergeDetails
                        @done="merged"
                        :participants="toMerge"
                        :main="mergeTo"
                    />
                </template>
            </WizardComp>
        </div>
    </ContentComp>
</template>
